import { useState, useEffect, useContext } from 'react';
import AuthContext from '../../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const FIELDS = [
    { key: 'company_name', label: 'Company Name', placeholder: 'e.g. Acme Pvt Ltd', required: true },
    { key: 'gstin', label: 'GSTIN', placeholder: 'e.g. 29ABCDE1234F1Z5', required: false, maxLength: 15, transform: v => v.toUpperCase().replace(/[^A-Z0-9]/g, '') },
    { key: 'address_line1', label: 'Address Line 1', placeholder: 'Street address', required: true },
    { key: 'address_line2', label: 'Address Line 2', placeholder: 'Apartment, suite, etc. (optional)', required: false },
    { key: 'city', label: 'City', placeholder: 'e.g. Bengaluru', required: true },
    { key: 'state', label: 'State', placeholder: 'e.g. Karnataka', required: true },
    { key: 'pincode', label: 'Pincode', placeholder: 'e.g. 560001', required: true, maxLength: 6, transform: v => v.replace(/\D/g, '') },
    { key: 'country', label: 'Country', placeholder: 'India', required: false },
];

const EMPTY = Object.fromEntries(FIELDS.map(f => [f.key, '']));

const BillingAddress = () => {
    const authCtx = useContext(AuthContext);
    const [form, setForm] = useState({ ...EMPTY, country: 'India' });
    const [saved, setSaved] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (!authCtx.token) return;
        fetch(`${API_URL}/auth/billing-address`, {
            headers: { 'Authorization': `Bearer ${authCtx.token}` },
        })
            .then(r => r.json())
            .then(data => {
                if (data.success && data.billing_address) {
                    setForm({ ...EMPTY, country: 'India', ...data.billing_address });
                    setSaved(data.billing_address);
                }
            })
            .catch(() => {});
    }, [authCtx.token]);

    const handleChange = (key, value, field) => {
        const v = field.transform ? field.transform(value) : value;
        setForm(prev => ({ ...prev, [key]: field.maxLength ? v.slice(0, field.maxLength) : v }));
    };

    const handleSave = async () => {
        const missing = FIELDS.filter(f => f.required && !form[f.key]?.trim());
        if (missing.length) { setMsg(`Please fill: ${missing.map(f => f.label).join(', ')}`); return; }

        setSaving(true); setMsg('');
        try {
            const res = await fetch(`${API_URL}/auth/billing-address`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authCtx.token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (data.success) {
                setSaved(data.billing_address);
                setEditing(false);
                setMsg('Billing address saved!');
                setTimeout(() => setMsg(''), 3000);
            } else { setMsg(data.message || 'Failed to save'); }
        } catch { setMsg('Error saving address'); }
        finally { setSaving(false); }
    };

    const hasSaved = saved && saved.address_line1;

    return (
        <div className="w-full mt-6 p-5 bg-white rounded-2xl shadow-[0_0_20px_rgba(120,179,243,0.08)] border border-[#E8F0FE]">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-[#505050] text-lg font-semibold">Billing Address</h3>
                {hasSaved && !editing && (
                    <button onClick={() => setEditing(true)} className="text-[#2766AA] text-sm font-semibold hover:underline">Edit</button>
                )}
            </div>

            {msg && <div className={`text-sm mb-3 ${msg.includes('saved') ? 'text-[#2E9E5C]' : 'text-red-500'}`}>{msg}</div>}

            {hasSaved && !editing ? (
                <div className="flex flex-col gap-1 text-sm text-[#505050]">
                    {saved.company_name && <span className="font-semibold">{saved.company_name}</span>}
                    <span>{saved.address_line1}{saved.address_line2 ? `, ${saved.address_line2}` : ''}</span>
                    <span>{saved.city}, {saved.state} {saved.pincode}</span>
                    <span>{saved.country || 'India'}</span>
                    {saved.gstin && <span className="text-[#2766AA] font-medium">GSTIN: {saved.gstin}</span>}
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-3">
                        {FIELDS.map(field => (
                            <div key={field.key} className={field.key === 'address_line1' || field.key === 'address_line2' ? 'col-span-2' : ''}>
                                <label className="text-[#737373] text-xs font-medium mb-1 block">
                                    {field.label}{field.required && <span className="text-red-400"> *</span>}
                                </label>
                                <input
                                    type="text"
                                    value={form[field.key]}
                                    onChange={e => handleChange(field.key, e.target.value, field)}
                                    placeholder={field.placeholder}
                                    maxLength={field.maxLength}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3]"
                                />
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2 mt-1">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-6 py-2 bg-[#2766AA] text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Address'}
                        </button>
                        {hasSaved && (
                            <button onClick={() => { setEditing(false); setForm({ ...EMPTY, country: 'India', ...saved }); }} className="px-6 py-2 text-[#737373] text-sm font-semibold rounded-lg border border-[#D0E7FF] hover:bg-[#F8FBFF]">
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingAddress;
